import HBInfoDialog from "@/components/HBModal/info";
import { Button, Form, Input, Space } from "antd";
import { omitBy } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { When } from "react-if";
import HBIcon from "../HBIcon";
import AuthSubjectInfo from "./authInfo";
import IdentityInput, { list } from "./identityInput";
import styles from "./index.module.less";
import RiskWarn from "./RiskWarn";
import getRules from "./rules";
import { CertSubjectSelect } from "./Select";
/**
 *
 * @param {Object} props
 * @param {string} props.secen 使用场景 (样式的区别)
 * @param {"buyVip"|"normal"} props.scene
 * @param {"personal"|"company"} props.type
 * @param {import("react").CSSProperties} props.footerStyle
 * @returns
 */
const TYPE = {
  personal: "personal", // 个人
  company: '"company"', //企业
};
export default function AuthSubjectForm({
  type,
  initValue = {},
  onSubmit,
  onCancel,
  footerStyle,
  scene = "normal",
  isBigCompany = false,
}) {
  const [currentType, setCurrent] = useState(type);
  const rules = getRules(currentType);
  const [identityType, setIdentityType] = useState(() => {
    let target = list?.find((item) => {
      return rules?.[item.value]?.[0].pattern.test(initValue?.id_no);
    });
    if (!initValue?.id_no || !target || currentType !== TYPE.personal) {
      return "ZHReg";
    }
    return target?.value;
  });

  const onSwitch = (value) => {
    setCurrent(value);
  };

  const formDataRef = useRef(initValue);

  const onValuesChange = useCallback((value, all) => {
    formDataRef.current = omitBy(all, (item) => !item);
  }, []);

  const formRef = useRef();

  // 设置初始值
  useEffect(() => {
    if (initValue && formRef.current) {
      const fieldDatas = [];
      for (const key in initValue) {
        if (Object.hasOwnProperty.call(initValue, key)) {
          const value = initValue[key];
          fieldDatas.push({
            name: key,
            value,
          });
        }
      }

      formRef.current.setFields(fieldDatas);
    }
  }, [initValue]);

  const onSelectOldRecord = useCallback((value) => {
    const defaultVal = value?.subject_info || {};
    const fieldDatas = [];
    for (const key in defaultVal) {
      if (Object.hasOwnProperty.call(defaultVal, key)) {
        const value = defaultVal[key];
        fieldDatas.push({
          name: key,
          value,
        });
      }
    }
    formDataRef.current = defaultVal;
    formRef.current.setFields(fieldDatas);
  }, []);
  // 提交按钮
  const onSubmitForm = () => {
    if (
      formDataRef.current.name.includes("公司") &&
      currentType === TYPE.personal
    ) {
      HBInfoDialog.open({
        width: 480,
        title: "风险提示",
        content: <RiskWarn></RiskWarn>,
        okText: "继续提交",
        cancelText: "重新填写",
        onOk: () => {
          onSubmit({ ...formDataRef.current, type: currentType });
        },
      });
    } else {
      onSubmit({ ...formDataRef.current, type: currentType });
    }
  };
  return (
    <div className={styles.container}>
      <div className={styles.warning}>
        <HBIcon className={styles.icon} type="ic_info" />
        <span className={styles.text}>
          下载前请务必填写授权书，否则无法获取商用保障权益
        </span>
      </div>
      <AuthSubjectInfo />
      <When condition={currentType === TYPE.personal && type === TYPE.company}>
        <div
          className={styles.switch}
          style={{
            marginTop: scene === "buyVip" ? 12 : 0,
          }}
        >
          <ul className={styles.desc}>
            <li>
              1、被授权方仅能为限单一主体（单个企业或个人），禁止共享或转让。
            </li>
            <li>
              2、若您填写个人信息，则本会员身份只可用于个人商用，不可再用于企业
            </li>
          </ul>
          <div className={styles.operation}>
            <a className={styles.button} onClick={() => onSwitch(TYPE.company)}>
              <HBIcon className={styles.icon} type="ic_refresh" />
              切换为企业
            </a>
          </div>
        </div>
      </When>
      <div className={styles.form}>
        {currentType === TYPE.personal ? (
          <Form
            key={currentType}
            ref={formRef}
            labelAlign={"left"}
            initialValues={initValue}
            onValuesChange={onValuesChange}
            autoComplete="off"
          >
            <Form.Item
              label="姓名"
              name="name"
              rules={rules.name}
              required={false}
            >
              <CertSubjectSelect
                type={currentType}
                className={styles.input}
                onSelect={onSelectOldRecord}
                placeholder="必填项，例如：花小瓣"
              />
            </Form.Item>
            <Form.Item
              label="身份证件"
              name="id_no"
              rules={[
                ...rules[identityType],
                {
                  required: true,
                  message: "请输入身份证件信息",
                  validateTrigger: ["onBlur", "onChange"],
                },
              ]}
              required={false}
            >
              <IdentityInput
                identityType={identityType}
                setIdentityType={setIdentityType}
              ></IdentityInput>
            </Form.Item>
            <Form.Item
              label="联系电话"
              name="phone"
              tooltip={formLabelTooltip("电话号码必须以”-“作为分隔符")}
              rules={rules.phone}
              required={false}
            >
              <Input
                className={styles.input}
                placeholder="必填项，例如：13512345678或0592-8263159"
                allowClear
              />
            </Form.Item>
          </Form>
        ) : (
          <Form
            key={currentType}
            labelAlign={"left"}
            onValuesChange={onValuesChange}
            ref={formRef}
            autoComplete="off"
            initialValues={initValue}
          >
            <Form.Item
              label="公司名称"
              name="name"
              required={false}
              rules={rules.name}
              tooltip={
                isBigCompany
                  ? null
                  : formLabelTooltip(
                      <div>
                        <HBIcon type="ic_refresh" style={{ paddingRight: 4 }} />
                        <span>无企业，切换为</span>
                        <a
                          onClick={() => onSwitch(TYPE.personal)}
                          style={{
                            cursor: "pointer",
                            textDecorationLine: "underline",
                          }}
                        >
                          个人
                        </a>
                      </div>
                    )
              }
            >
              <CertSubjectSelect
                type={currentType}
                className={styles.input}
                onSelect={onSelectOldRecord}
                placeholder="必填项，例如：花瓣科技有限公司"
              />
            </Form.Item>
            <Form.Item
              label="单位代码"
              name="id_no"
              rules={rules.id_no}
              tooltip={formLabelTooltip("指统一社会信用代码")}
              required={false}
            >
              <Input
                className={styles.input}
                placeholder="必填项，例如：912345678912345678"
                allowClear
              />
            </Form.Item>
            <Form.Item
              label="联系电话"
              name="phone"
              rules={rules.phone}
              tooltip={formLabelTooltip("电话号码必须以”-“作为分隔符")}
              required={false}
            >
              <Input
                className={styles.input}
                placeholder="必填项，例如：13512345678或0592-8263159"
                allowClear
              />
            </Form.Item>
          </Form>
        )}
      </div>
      <div className={styles.note}>请确认信息填写正确，提交后不可修改</div>

      <Space className={styles.footer} style={footerStyle}>
        <Button style={{ padding: "9px 30px" }} onClick={onCancel}>
          取消
        </Button>
        <Button
          type="primary"
          style={{ padding: "9px 30px" }}
          onClick={async () => {
            try {
              await formRef.current.validateFields();
              onSubmitForm();
            } catch (error) {}
          }}
        >
          提交
        </Button>
      </Space>
    </div>
  );
}

function formLabelTooltip(content) {
  return {
    icon: <HBIcon type="ic_help" />,
    title: content,
    overlayClassName: styles.tooltip,
  };
}
